import React from 'react';
import { AlertCircle, Home, RefreshCw } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface BlogErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

/**
 * BlogErrorFallback - Custom error fallback UI for blog components
 *
 * Provides a blog-specific error message with navigation options.
 * Allows users to retry the current view or navigate back to the blog list.
 *
 * @param error - The error that was caught
 * @param resetError - Function to reset the error boundary and retry
 */
export default function BlogErrorFallback({ error, resetError }: BlogErrorFallbackProps): React.ReactElement {
  const navigate = useNavigate();

  const handleGoHome = () => {
    navigate('/blog');
    resetError();
  };

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-8">
        <div className="flex flex-col items-center text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mb-4">
            <AlertCircle className="w-8 h-8 text-red-600" aria-hidden="true" />
          </div>

          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Unable to load blog content
          </h2>

          <p className="text-gray-600 mb-6 max-w-md">
            We encountered an error while loading this blog post. This might be a temporary issue.
          </p>

          {process.env.NODE_ENV === 'development' && (
            <details className="mb-6 text-left w-full max-w-md">
              <summary className="text-sm text-gray-600 cursor-pointer hover:text-gray-800 mb-2 font-medium">
                Error Details (Development Only)
              </summary>
              <div className="mt-2 p-4 bg-gray-50 rounded border border-gray-200">
                <p className="text-sm font-semibold text-gray-700 mb-2">
                  {error.message}
                </p>
                <pre className="text-xs text-gray-600 overflow-x-auto whitespace-pre-wrap">
                  {error.stack}
                </pre>
              </div>
            </details>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={resetError}
              className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors font-medium"
              aria-label="Retry loading the blog content"
            >
              <RefreshCw className="w-4 h-4" aria-hidden="true" />
              Try Again
            </button>

            <button
              onClick={handleGoHome}
              className="inline-flex items-center gap-2 px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              aria-label="Return to blog home"
            >
              <Home className="w-4 h-4" aria-hidden="true" />
              Back to Blog
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
